import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';

export type FvLogoSize = 'sm' | 'md';
export type FvLogoTheme = 'default' | 'inverted';
export type FvLogoType = 'icon' | 'full';

/**
 * Renders the company logo from the white-label asset convention: every build
 * ships `assets/logo/logo.svg` (full lockup) and
 * `assets/logo/logo-sidebar-collapsed.svg` (icon-only) — Finverity defaults on
 * plain builds, replaced per tenant by the white-labeling asset copy.
 * Consumers may point `srcFull`/`srcIcon` elsewhere.
 */
@Component({
  selector: 'fv-logo',
  standalone: true,
  templateUrl: './logo.component.html',
  styleUrl: './logo.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FvLogoComponent {
  /** Logo size: sm = 44px height, md = 72px height */
  readonly size = input<FvLogoSize>('sm');

  /** Theme hook (kept for API compatibility; assets carry their own colours) */
  readonly theme = input<FvLogoTheme>('default');

  /** Logo type: icon = icon only, full = icon + company name */
  readonly type = input<FvLogoType>('full');

  /** Asset for the full lockup (tenant-overridden at build time) */
  readonly srcFull = input<string>('assets/logo/logo.svg');

  /** Asset for the icon-only variant (tenant-overridden at build time) */
  readonly srcIcon = input<string>('assets/logo/logo-sidebar-collapsed.svg');

  /** Computed logo height in px */
  readonly logoHeight = computed(() => (this.size() === 'md' ? 72 : 44));

  /** Active asset for the current type */
  readonly src = computed(() => (this.type() === 'icon' ? this.srcIcon() : this.srcFull()));
}
